import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import type { ExperimentDocument, ExperimentInputPayload, ExperimentTransitionPayload } from '../../experimentTypes';
import type { FunnelABTestDecisionRow } from '../../types';
import { ExperimentCard } from '../ExperimentCard';
import { EXPERIMENTS_TEST_IDS } from '../../experimentsTestIds';
import type { RowPrefillRequest } from '../../rowServedPrefill';
import { usePricingDismissals } from '../../hooks/usePricingDismissals';
import type { GalleryTemplateId, GalleryTemplatePrefill } from '../../pricingGallery';
import type { MaterializeMode } from '../../pricingMaterialize';
import { PricingGallerySection, type PricingGalleryRegistryInfo } from './PricingGallerySection';
import { ClaimChip } from './ClaimChip';

// Lazy: the calculator pulls firebase-core + stripe-connect into the graph —
// the hub's default tab must not pay for them (they load with ?tab=pricing).
const PricingCalculatorSection = React.lazy(() =>
  import('./PricingCalculatorSection').then(module => ({ default: module.PricingCalculatorSection })),
);
// Phase 2 remainder: cells + candidates + «Already tested» — same lazy rule
// (the section pulls the insights client + the recommendations engine).
const PricingRecommendationsSection = React.lazy(() =>
  import('./PricingRecommendationsSection').then(module => ({ default: module.PricingRecommendationsSection })),
);
// AC-087 (owner amendment, checkpoint 2026-09-02): the from-scratch entry —
// the confirm in manual mode; lazy for the same firebase/stripe graph reason.
const PricingRegisterConfirmDialog = React.lazy(() =>
  import('./PricingRegisterConfirmDialog').then(module => ({ default: module.PricingRegisterConfirmDialog })),
);
// Phase 4 W2 (AC-025/091): real guardrail readouts on running experiments —
// lazy because the panel pulls the insights client into the graph.
const PricingMonitoringPanel = React.lazy(() =>
  import('./PricingMonitoringPanel').then(module => ({ default: module.PricingMonitoringPanel })),
);
// Phase 4 W3 (AC-027..029): the M2 hint cards — lazy for the same
// firebase/stripe palette graph reason as the calculator.
const PricingHintsSection = React.lazy(() =>
  import('./PricingHintsSection').then(module => ({ default: module.PricingHintsSection })),
);
// P6 (AC-032): the create-at-the-end confirm. Lazy for the same reason as the
// calculator — the host calls `useStripeConnect` + `PaywallProductService`, and
// a hook cannot be lazy, so the whole bridging container loads on demand.
const PricingCreateProductsDialogHost = React.lazy(() =>
  import('./PricingCreateProductsDialogHost').then(module => ({ default: module.PricingCreateProductsDialogHost })),
);

/**
 * Pricing tab of the Experiments hub (plan §14.2, mocks hub-pricing-entry /
 * planner-monitoring / cold-start-checklist).
 *
 * One registry (D47): pricing experiments are ordinary registry documents of
 * type 'pricing'; this section filters and presents them with pricing-specific
 * framing — the planner wizard, honest guardrail states for running
 * experiments, and the FP-8 cold-start checklist when nothing exists yet.
 */

export interface PricingPlannerSectionProps {
  projectId: string;
  /** Phase 2-min (AC-038): S13 row seeds parsed from the hub URL. */
  rowPrefill?: RowPrefillRequest | null;
  experiments: ExperimentDocument[];
  /** True while the registry list loads — the cold-start blocks wait for it (no flash on projects that have experiments). */
  experimentsLoading?: boolean;
  decisions: FunnelABTestDecisionRow[] | null;
  onCreate: (input: ExperimentInputPayload) => Promise<void>;
  onTransition: (id: string, payload: ExperimentTransitionPayload) => Promise<unknown>;
  onEdit: (experiment: ExperimentDocument) => void;
  onResearchGenerated: () => void;
}

/** Cold-start steps (FP-8 confirm-not-form; every locked step names its unlock). */
const COLD_START_STEPS = [
  {
    title: '1 · Paywall products',
    body: 'Create paywall products on your payment provider (Stripe test mode is fine — rename anytime). The planner only ever selects among these preconfigured offers.',
  },
  {
    title: '2 · Baseline menu',
    body: 'Confirm the offer menu on your paywall screen — this is the permanent control every future experiment compares against.',
  },
  {
    title: '3 · Decision & exposure logging',
    body: 'ON by default: every paywall decision records the menu shown, rule id and propensity — the fields that make future offline policy evaluation possible. Nothing to configure.',
  },
  {
    title: '4 · Collect baseline evidence',
    body: 'The planner unlocks at 100 paywall users OR 25 conversions. Until then candidates use external priors only and stay labeled "scenario (external prior)". Meanwhile: finish the launch checklist or connect the ads module for CAC/pLTV context.',
    locked: true,
  },
] as const;

export const PricingPlannerSection: React.FC<PricingPlannerSectionProps> = ({
  projectId,
  rowPrefill,
  experiments,
  experimentsLoading = false,
  decisions,
  onCreate,
  onTransition,
  onEdit,
  onResearchGenerated,
}) => {
  const [manualOpen, setManualOpen] = useState(false);
  /* Phase 5 (AC-030.6): the registry state the calculator reports, which is
     what the gallery branches its primary on. The gallery's OTHER handoff —
     «Use this plan in the calculator» — is gone with the 2026-09-19 port: the
     calculator's products are the project's paywall products, and a template
     is a set of prices nobody has registered yet. */
  const [registryInfo, setRegistryInfo] = useState<PricingGalleryRegistryInfo | null>(null);
  // P6 (AC-032): the create-at-the-end confirm request + the post-creation
  // re-pull token + the success snackbar. The planner owns this state; the
  // lazy host owns only the hook bridging.
  const [createRequest, setCreateRequest] = useState<{ prefill: GalleryTemplatePrefill; categoryLabel?: string } | null>(null);
  /** The anchor the gallery's ladder is derived from — the calculator's presets
   *  price the same templates from it, so one tab never offers «Classic» twice
   *  at two prices (review round 1, finding 3). `null` until the gallery
   *  reports one, i.e. whenever the cold-start gallery is not rendered. */
  const [galleryAnchor, setGalleryAnchor] = useState<number | null>(null);
  /** The template the gallery BADGES. The calculator's preset row puts its one
   *  contained primary on it, so the tab emphasises one menu and not two
   *  (tier-3 REVIEW, finding M4). `null` whenever the gallery is not rendered,
   *  and the preset row then falls back to the reference template. */
  const [galleryPick, setGalleryPick] = useState<GalleryTemplateId | null>(null);
  const [registryReloadToken, setRegistryReloadToken] = useState(0);
  const [createdCount, setCreatedCount] = useState<number | null>(null);
  /** P7 (AC-104): the mode the reported count was created in — the snackbar
   * says «LIVE» only when the run actually was live. */
  const [createdMode, setCreatedMode] = useState<MaterializeMode>('test');
  /** P7 tier-3 fix (P1-1): how many rows of the reported run failed — a
   * partial run closed must not be announced as a plain, unqualified success. */
  const [createdFailed, setCreatedFailed] = useState(0);
  /** How many paywall products this session created — the FLOOR under the
   * registry state reported to the gallery (see `galleryRegistry`). */
  const [createdTotal, setCreatedTotal] = useState(0);
  const handlePaletteLoaded = useCallback((info: PricingGalleryRegistryInfo) => setRegistryInfo(info), []);
  /* ONE create-products host for the whole tab. The gallery asks for it with a
     category label, the calculator's preset tiles without one (they have no
     category input), and both land in the same confirm — which is what keeps
     `createdTotal` a floor under the registry state for BOTH doors: a failed
     re-pull after a run cannot put either surface back on «Create these
     products» with every row re-ticked and duplicate real Stripe objects
     (review round 1, finding 2). */
  const handleCreateProducts = useCallback((prefill: GalleryTemplatePrefill, categoryLabel?: string) => {
    setCreateRequest({ prefill, categoryLabel });
  }, []);
  /** AC-032.4: the calculator re-pulls the registry (created offers become
   * `observed`) and reports the new count, which flips the gallery's note and
   * primary to the `registryHasProducts` branch. */
  const handleCreated = useCallback((count: number, mode: MaterializeMode, failed: number) => {
    setRegistryReloadToken(current => current + 1);
    setCreatedCount(count);
    setCreatedMode(mode);
    setCreatedFailed(failed);
    setCreatedTotal(current => Math.max(current, count));
  }, []);
  /**
   * The registry state the gallery branches on. `onPaletteLoaded` alone is not
   * safe as the single source: the calculator's catch branch reports
   * `{hasRegisteredProducts: false, count: 0}` when the registry read FAILS, so
   * a failed re-pull right after a run would put the gallery back on «Create
   * these products in Stripe test» with every row re-ticked — a second confirm
   * would then duplicate what already exists. Anything this session actually
   * created is therefore a floor: known-created products cannot un-exist
   * because a read failed.
   */
  const galleryRegistry = useMemo<PricingGalleryRegistryInfo | null>(() => {
    if (createdTotal === 0) return registryInfo;
    return {
      hasRegisteredProducts: true,
      count: Math.max(registryInfo?.count ?? 0, createdTotal),
    };
  }, [registryInfo, createdTotal]);
  /** Phase 4 (AC-095..097): ONE durable dismissal store for the whole tab —
   * candidates and hints share it (one GET, no write races between sections). */
  const dismissalsApi = usePricingDismissals(projectId);
  const pricingExperiments = experiments
    .filter(experiment => experiment.type === 'pricing' && experiment.state !== 'archived')
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const running = pricingExperiments.filter(experiment => experiment.state === 'running');
  const coldStart = !experimentsLoading && pricingExperiments.length === 0;

  return (
    <Box data-testid={EXPERIMENTS_TEST_IDS.pricingSection}>
      {/* Phase 5, review A-F1 (owner-delegated decision 2026-09-04): in cold start
          «Nothing exists yet» comes FIRST — the M0 gallery leads and the
          calculator follows it, still fully rendered (FP-1). The gallery's only
          handoff is now the create-products confirm below; «Use this plan in
          the calculator», and the scroll that went with it, went with the
          2026-09-19 unit-economics port. */}
      {coldStart && (
        <PricingGallerySection
          projectId={projectId}
          registry={galleryRegistry}
          onCreateProducts={handleCreateProducts}
          onAnchorChange={setGalleryAnchor}
          onPickChange={setGalleryPick}
        />
      )}

      {createRequest && (
        <React.Suspense fallback={null}>
          <PricingCreateProductsDialogHost
            open
            projectId={projectId}
            prefill={createRequest.prefill}
            categoryLabel={createRequest.categoryLabel}
            onClose={() => setCreateRequest(null)}
            onCreated={handleCreated}
          />
        </React.Suspense>
      )}

      {/* AC-032.4: the closed dialog's only trace — the created count. P7
          tier-3 fix (P1-1): a partial run (createdFailed > 0) must not read as
          a plain, unqualified success — «N of M … created — K failed». */}
      <Snackbar
        open={createdCount !== null}
        autoHideDuration={6000}
        onClose={() => setCreatedCount(null)}
        message={createdCount === null
          ? ''
          : createdFailed > 0
            ? `${createdCount} of ${createdCount + createdFailed} ${createdMode === 'live' ? 'LIVE ' : ''}products created — ${createdFailed} failed`
            : `${createdCount} ${createdMode === 'live' ? 'LIVE ' : ''}product${createdCount === 1 ? '' : 's'} created`}
        ContentProps={{ 'data-testid': EXPERIMENTS_TEST_IDS.pricingGalleryCreateSnackbar } as never}
      />

      {/* Phase 0 (plan v2 2026-08-31): standalone calculator — local scenario math,
          prices prefilled from paywallProducts, no warehouse reads, no writes. */}
      <React.Suspense fallback={null}>
        <PricingCalculatorSection
          projectId={projectId}
          rowPrefill={rowPrefill ?? undefined}
          reloadToken={registryReloadToken}
          onPaletteLoaded={handlePaletteLoaded}
          onCreateProducts={handleCreateProducts}
          presetAnchor={galleryAnchor ?? undefined}
          presetPrimary={galleryPick ?? undefined}
        />
      </React.Suspense>

      {/* Phase 2 remainder (plan §2/§3, mocks approved 2026-09-02): decision
          cells + ranked candidates + the read-only «Already tested» view. */}
      {/* Phase 3a (AC-085, approved round-3 decision 2): the legacy wizard and
          its «Plan pricing experiment» button are retired — candidates are the
          only entry to draft registration (v2 kills manual-id authoring). */}
      <React.Suspense fallback={null}>
        <PricingRecommendationsSection
          projectId={projectId}
          rowPrefill={rowPrefill ?? undefined}
          experiments={experiments}
          onRegister={onCreate}
          dismissalsApi={dismissalsApi}
        />
      </React.Suspense>

      {/* Phase 4 W3 (approved round-4 decision 4): the hints block sits under
          the candidates; regenerated on open; dismissed slugs filtered. */}
      <React.Suspense fallback={null}>
        <PricingHintsSection
          projectId={projectId}
          rowPrefill={rowPrefill ?? undefined}
          dismissalsApi={dismissalsApi}
          onRegister={onCreate}
        />
      </React.Suspense>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Pricing experiments</Typography>
          <ClaimChip tone="scenario" label="basis: gross · audit" data-testid={EXPERIMENTS_TEST_IDS.pricingBasisChip} />
        </Stack>
        {/* AC-087: a draft can start WITHOUT a recommendation — the same
            confirm in manual mode (products picked, axis a readable choice;
            no free-text ids — the wizard stays retired). */}
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          data-testid={EXPERIMENTS_TEST_IDS.pricingManualDraftButton}
          onClick={() => setManualOpen(true)}
        >
          New pricing experiment
        </Button>
      </Stack>

      {manualOpen && (
        <React.Suspense fallback={null}>
          <PricingRegisterConfirmDialog
            open
            manual
            projectId={projectId}
            scope={null}
            candidate={null}
            cell={null}
            windowLabel=""
            inputHash=""
            onClose={() => setManualOpen(false)}
            onSubmit={onCreate}
          />
        </React.Suspense>
      )}

      {coldStart && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={EXPERIMENTS_TEST_IDS.pricingColdStart}>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>Getting started with price experiments</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Four confirmations unlock the planner — everything is pre-filled, nothing is applied silently.
          </Typography>
          <Stack spacing={1.25}>
            {COLD_START_STEPS.map((step, index) => (
              <Stack
                key={step.title}
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
                data-testid={EXPERIMENTS_TEST_IDS.pricingColdStartStep(index + 1)}
              >
                <Chip
                  size="small"
                  label={'locked' in step && step.locked ? 'locked' : 'confirm'}
                  color={'locked' in step && step.locked ? 'default' : 'primary'}
                  variant="outlined"
                />
                <Box>
                  <Typography variant="body2" fontWeight={600}>{step.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{step.body}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Phase 4 W2 (AC-025): served guardrail readings per running experiment;
          typed absences inside the panel, never fake zeros. */}
      {running.map(experiment => (
        <React.Suspense key={`monitor-${experiment.id}`} fallback={null}>
          <PricingMonitoringPanel projectId={projectId} experiment={experiment} />
        </React.Suspense>
      ))}

      <Stack spacing={1.5} data-testid={EXPERIMENTS_TEST_IDS.pricingList}>
        {coldStart && (
          <Alert severity="info" data-testid={EXPERIMENTS_TEST_IDS.pricingEmptyState}>
            No pricing experiments yet. Register one from a recommended candidate above, or start
            from scratch with «New pricing experiment» — either way the prefilled confirm carries
            offer identity, a single treatment axis, the plan-mix metric, and delayed guardrails;
            screens are bound at launch.
          </Alert>
        )}
        {pricingExperiments.map(experiment => (
          <ExperimentCard
            key={experiment.id}
            projectId={projectId}
            experiment={experiment}
            decisions={decisions}
            onTransition={onTransition}
            onEdit={onEdit}
            onResearchGenerated={onResearchGenerated}
          />
        ))}
      </Stack>
    </Box>
  );
};
